import { useState } from "react";
import { api } from "@/lib/api";
import { BRAND } from "@/lib/brand";
import { toast } from "sonner";
import { Loader2, X } from "lucide-react";

export default function ChangePasswordDialog({ open, onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  const reset = () => {
    setCurrent("");
    setNext("");
    setConfirm("");
    setError("");
    setShowCurrent(false);
    setShowNext(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose?.();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!current) return setError("Please enter your current password.");
    if (next.length < 10)
      return setError("New password must be at least 10 characters.");
    if (next !== confirm)
      return setError("New password and confirmation do not match.");
    if (next === current)
      return setError("New password must be different from current.");

    setSubmitting(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next,
      });
      toast.success("Password updated", {
        description: "Use your new password next time you sign in.",
      });
      setSubmitting(false);
      reset();
      onClose?.();
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Could not update password."
      );
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50"
      onClick={handleClose}
      data-testid="change-password-dialog"
    >
      <div
        className="bg-white rounded-2xl w-full max-w-md shadow-xl border border-slate-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="px-6 py-4 text-white rounded-t-2xl flex items-center justify-between"
          style={{
            background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
          }}
        >
          <h3 className="text-base font-semibold">Change password</h3>
          <button
            onClick={handleClose}
            className="text-white/80 hover:text-white"
            disabled={submitting}
          >
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="change-password-form">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Current password
            </label>
            <div className="relative">
              <input
                type={showCurrent ? "text" : "password"}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                data-testid="cp-current-input"
                className="w-full px-3 py-2 pr-16 rounded-lg border border-slate-300 text-slate-900 focus:outline-none focus:ring-2"
                style={{ "--tw-ring-color": BRAND.teal }}
                disabled={submitting}
                autoFocus
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowCurrent((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-500 hover:text-slate-700 px-2 py-1"
                tabIndex={-1}
              >
                {showCurrent ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              New password
            </label>
            <div className="relative">
              <input
                type={showNext ? "text" : "password"}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                placeholder="At least 10 characters"
                data-testid="cp-new-input"
                className="w-full px-3 py-2 pr-16 rounded-lg border border-slate-300 text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2"
                style={{ "--tw-ring-color": BRAND.teal }}
                disabled={submitting}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowNext((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-xs font-medium text-slate-500 hover:text-slate-700 px-2 py-1"
                tabIndex={-1}
              >
                {showNext ? "Hide" : "Show"}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Confirm new password
            </label>
            <input
              type={showNext ? "text" : "password"}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              data-testid="cp-confirm-input"
              className="w-full px-3 py-2 rounded-lg border border-slate-300 text-slate-900 focus:outline-none focus:ring-2"
              style={{ "--tw-ring-color": BRAND.teal }}
              disabled={submitting}
              autoComplete="new-password"
            />
          </div>

          {error && (
            <div
              className="text-sm px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700"
              data-testid="cp-error"
            >
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="px-4 py-2 rounded-lg text-slate-700 border border-slate-200 hover:bg-slate-50 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="cp-submit-button"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white font-semibold transition disabled:opacity-70"
              style={{
                background: `linear-gradient(135deg, ${BRAND.teal} 0%, ${BRAND.purple} 100%)`,
              }}
            >
              {submitting && <Loader2 className="animate-spin" size={14} />}
              Update password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
